import { useMemo, useState } from 'react';
import { SearchableSelect } from '@shared/components';
import { useAllowedParentDealers } from '../hooks/useDealersQuery';
import { useDealersByIds } from '../hooks/useDealersByIds';
import { allowedParentType } from '../utils/dealerParentageRules';
import type { Dealer, DealerType } from '../types/dealers.types';

interface AllowedParentOption {
  id: string;
  name: string;
}

interface AllowedParentSelectProps {
  type: DealerType;
  value: string | undefined;
  onChange: (id: string | undefined) => void;
  excludeId?: string;
  disabled?: boolean;
  required?: boolean;
  minChars?: number;
}

/**
 * Owning-parent picker for DealerForm. Wraps SearchableSelect like
 * DealerSelect, but the candidate query is driven by the dealer-parentage
 * matrix (allowedParentType(type)) rather than the legacy is_group filter.
 * Error/hint rendering is left to the caller, matching every other field in
 * DealerForm.
 */
export function AllowedParentSelect({
  type,
  value,
  onChange,
  excludeId,
  disabled = false,
  required = false,
  minChars = 2,
}: AllowedParentSelectProps) {
  const [term, setTerm] = useState('');
  const parentType = allowedParentType(type);
  const canQuery = term.length >= minChars;

  const { data, isFetching } = useAllowedParentDealers(parentType, term, canQuery);

  // Resolve the label for a preset value (edit mode) without requiring the
  // user to search first — the dealer is serialized with parent_dealer_id,
  // but not its name.
  const presetIds = useMemo(() => (value ? [value] : []), [value]);
  const { data: presetDealers } = useDealersByIds(presetIds);

  const selectedOption: AllowedParentOption | null = useMemo(() => {
    if (!value) return null;
    const fromSearch = (data?.data ?? []).find((d) => d.id === value);
    if (fromSearch) return { id: fromSearch.id, name: fromSearch.name };
    const fromPreset = (presetDealers ?? []).find((d) => d.id === value);
    if (fromPreset) return { id: fromPreset.id, name: fromPreset.name };
    return { id: value, name: value };
  }, [value, data, presetDealers]);

  if (parentType === null) {
    return null;
  }

  const excludeIds = excludeId ? [excludeId] : [];

  return (
    <SearchableSelect<AllowedParentOption | Dealer>
      options={canQuery ? (data?.data ?? []) : selectedOption ? [selectedOption] : []}
      value={selectedOption}
      onChange={(next) => onChange(next?.id)}
      getOptionValue={(d) => d.id}
      getOptionLabel={(d) => d.name}
      onSearchChange={setTerm}
      loading={isFetching}
      excludeValues={excludeIds}
      clearable={!required}
      disabled={disabled}
      placeholder="Search dealers by name, code, city, or postcode..."
      emptyLabel={canQuery ? 'No dealers match.' : `Type at least ${minChars} characters to search.`}
      renderOption={(d) => (
        <div className="min-w-0 text-left w-full">
          <div className="font-semibold text-sm">{d.name}</div>
          {'dealer_code' in d && d.dealer_code && (
            <div className="text-xs text-mp-muted flex gap-2">
              <span className="font-mono">{d.dealer_code}</span>
              {'city' in d && d.city && <span>&middot; {d.city}</span>}
              {'postcode' in d && d.postcode && <span>&middot; {d.postcode}</span>}
            </div>
          )}
        </div>
      )}
      id="parent_dealer_id"
    />
  );
}
